import { cx } from '@/lib/cx';

/*
 * Two sizes as whole class strings, never an override appended by a caller:
 * two utilities setting one property race the emitted stylesheet's order, so
 * `h-4 … h-3.5` is decided by Tailwind, not by the caller.
 */
const SIZES = {
  /* The filter-count dot on the Filters button (`ColumnFilter.tsx`), exactly. */
  md: 'h-4 min-w-4 px-1 text-2xs',
  /* Pinned to a 16px icon's corner in the collapsed sidebar rail. */
  sm: 'h-3.5 min-w-3.5 px-0.5 text-[0.625rem] leading-none',
} as const;

/**
 * A solid NEUTRAL count, the same shape as the filter-count dot on the Filters
 * button. Used where a row or a control needs to say "N things are waiting
 * here". ⚠️ *SUPERSEDED 2026-10-11: brand-accent fill.* The accent is reserved
 * for primary actions, the active nav row and focus, so a count in the accent
 * beside an active nav row would read as a second active state.
 *
 * Renders NOTHING for `null`, `undefined` or `0`: an unread count is not a
 * zero, and a zero is not news. It is `aria-hidden` — the caller puts the
 * number into the control's accessible name, so a screen reader hears it once,
 * in a sentence, rather than as a bare number after the label.
 *
 * Caps at `99+` so a large backlog cannot widen the row it sits in.
 */
export function CountBadge({
  count,
  className,
  size = 'md',
}: {
  count: number | null | undefined;
  /** Placement only (margin, position). Sizing goes through `size`. */
  className?: string;
  size?: keyof typeof SIZES;
}) {
  if (count === null || count === undefined || !Number.isFinite(count) || count <= 0) {
    return null;
  }
  return (
    <span
      aria-hidden="true"
      className={cx(
        'grid place-items-center rounded-full bg-fg-secondary font-semibold text-fg-inverse tabular-nums',
        SIZES[size],
        className,
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}
